import { useState } from "react"
import { array, shape, number } from "prop-types"
import { pluralize } from "utils/helpers"
import { DynamicDataView } from "components/ui"
import { camelToSnake } from "utils/strings"
import { api } from "utils/api"
import { StackView } from "@planningcenter/tapestry-react"

let fetchTimeout = null

const PeopleIndex = ({ people: peopleFromProps, pagination: paginationFromProps }) => {
  const [pagination, setPagination] = useState(paginationFromProps)
  const [isLoading, setIsLoading] = useState(false)
  const [people, setPeople] = useState(peopleFromProps)

  const renderAvatar = (row, _col) => (
    <img
      src={row.avatarUrl}
      alt={`${row.firstName} ${row.lastName}`}
      className="circle"
      css={{ height: 36, width: 36 }}
    />
  )

  const columns = [
    {
      cell: "avatarUrl",
      render: renderAvatar,
      className: "pl-2 pr-0"
    },
    {
      cell: "firstName",
      label: "First name",
      isSortable: true
    },
    {
      cell: "lastName",
      label: "Last name",
      isSortable: true
    },
    {
      cell: "emailAddress",
      label: "Email address"
    },
    {
      cell: "phoneNumber",
      label: "Phone number"
    }
  ]

  const handleRowClick = row => window.location.assign(`${row.paths.personPath}`)

  const fetchPeople = async ({ searchValue, sortBy, sortDir, page = 1, scope }) => {
    const params = [
      ...(searchValue && [`search=${searchValue}`]),
      ...(sortBy && [`sort_by=${camelToSnake(sortBy)}`]),
      ...(sortDir && [`sort_dir=${sortDir}`]),
      ...(sortDir && [`page=${page}`]),
      ...(scope && [`role=${scope}`])
    ]

    const paramsToString = params.length > 0 ? `?${params.join("&")}` : ""

    return await api.get(`${window.location}${paramsToString}`)
  }

  const delayFetchPeople = ({ searchValue, sortBy, sortDir, scope }) => {
    const promise = new Promise((resolve, _reject) => {
      fetchTimeout = setTimeout(() => {
        resolve(fetchPeople({ searchValue, sortBy, sortDir, scope }))
      }, 1500)
    })

    return promise
  }

  const handleSearch = async ({ searchValue: value, sortBy, sortDir, scope }) => {
    if (fetchTimeout) {
      clearTimeout(fetchTimeout)
    }

    setIsLoading(true)
    const searchValue = value.trim()
    let response = []

    if (searchValue < 1) {
      response = await delayFetchPeople({ searchValue, sortBy, sortDir, scope: scope.roles })
    } else {
      response = await delayFetchPeople({ searchValue, sortBy, sortDir, scope: scope.roles })
    }
    setIsLoading(false)

    setPeople(response.people)
    setPagination(response.pagination)
    return response.people
  }

  const handleSortClick = async ({ sortBy, sortDir, searchValue, scope }) => {
    setIsLoading(true)

    const response = await fetchPeople({
      searchValue,
      sortBy,
      sortDir,
      scope: scope.roles,
      page: 1
    })

    setIsLoading(false)
    setPeople(response.people)
    setPagination(response.pagination)
  }

  const handlePaginateClick = async ({ searchValue, sortBy, sortDir, page, scope }) => {
    setIsLoading(true)

    const response = await fetchPeople({
      searchValue,
      sortBy,
      sortDir,
      page,
      scope: scope.roles
    })

    setIsLoading(false)
    setPeople(response.people)
    setPagination(response.pagination)
  }

  const handleScopeChange = async ({ searchValue, sortBy, sortDir, page: _page = 1, scope }) => {
    setIsLoading(true)

    const response = await fetchPeople({
      searchValue,
      sortBy,
      sortDir,
      page: 1,
      scope: scope.roles
    })

    setIsLoading(false)
    setPeople(response.people)
    setPagination(response.pagination)
  }

  const renderPrimaryField = item => {
    return (
      <StackView direction="horizontal" spacing={2} alignItems="center">
        {renderAvatar(item)}
        <StackView>
          <div className="fw-b">
            {item.firstName} {item.lastName}
          </div>
          <div className="card__secondary">{item.emailAddress}</div>
          <div className="card__secondary">{item.phoneNumber}</div>
        </StackView>
      </StackView>
    )
  }

  return (
    <div>
      <DynamicDataView
        heading={{
          useCounter: true,
          headerText: pluralize("person", "people", pagination.count),
          style: { padding: "12px" }
        }}
        searchPlacement="aboveHeader"
        columns={columns}
        rows={people}
        onRowClick={handleRowClick}
        onSortClick={handleSortClick}
        onSearchChange={handleSearch}
        onPaginateClick={handlePaginateClick}
        pagination={pagination}
        defaultSort="lastName"
        isLoading={isLoading}
        emptyTableMessage="There are no results that match your current filters."
        emptyTableImageUrl="/static/blank-states/no-filtered-results.svg"
        scopeByFilters={[
          {
            name: "roles",
            options: [
              { value: "all", label: "All roles" },
              { value: "congregants", label: "Congregants" },
              { value: "managers", label: "Managers" },
              { value: "administrators", label: "Administrators" }
            ]
          }
        ]}
        onScopeChange={handleScopeChange}
        primaryField={renderPrimaryField}
      />
    </div>
  )
}

PeopleIndex.propTypes = {
  people: array,
  pagination: shape({
    nextPage: number,
    prevPage: number,
    totalPages: number,
    currentPage: number
  })
}

export default PeopleIndex
